import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  styled,
  Switch,
  SwitchProps,
  Typography,
} from "@mui/material";
import { CustomTextField, useStyles } from "./style";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { IMaskInput } from "react-imask";
import moment, { Moment } from "moment-timezone";
import { IBookingSettings, ISelectedService } from "pages/booking/interface";
import appointmentServices from "servers/appointment";
import websocketServices from "servers/websocket";
import { v4 as uuidv4 } from "uuid";
import {
  calStartTimeOfService,
  convertTo24Hour,
  convertToPhoneNumber,
  isValidPhoneNumber,
} from "common/utils";
import useWebSocket from "react-use-websocket";
import {
  BOOKING_SOURCE,
  GUEST_INFO,
  WS_ACTION,
  WS_ACTION_TYPE,
  X_Tenant_Id,
} from "common/constant";
import authServices from "servers/auth";
import businessServices from "servers/business";
import ServicesInformation from "../ServicesInformation";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";

interface IProps {
  selectedDateTime: { date?: Moment; time: string };
  selectedServices: Array<ISelectedService>;
  setSelectedServices: (agr: Array<ISelectedService>) => void;
  bookingForm: {
    submitting: boolean;
    submitted: boolean;
    data: {
      name: string;
      phone: string;
      email: string;
      comments: string;
      firstName: string;
      lastName: string;
      birthday: Moment | null;
    };
  };
  setBookingForm: (agr: {
    submitting: boolean;
    submitted: boolean;
    data: {
      name: string;
      phone: string;
      email: string;
      comments: string;
      firstName: string;
      lastName: string;
      birthday: Moment | null;
    };
  }) => void;
  isValidForm: boolean;
  customerInfo: any;
  setCustomerInfo: Dispatch<SetStateAction<any>>;
  timezone: string;
  unixPickDateTime: number;
  bookingSettings: IBookingSettings;
}

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 35,
  height: 20,
  padding: 0,
  marginRight: 8,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "1px 2px",
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#0B70E1",
        opacity: 1,
        border: 0,
        ...theme.applyStyles("dark", {
          backgroundColor: "#2ECA45",
        }),
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
      ...theme.applyStyles("dark", {
        color: theme.palette.grey[600],
      }),
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
      ...theme.applyStyles("dark", {
        opacity: 0.3,
      }),
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 18,
    height: 18,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    ...theme.applyStyles("dark", {
      backgroundColor: "#39393D",
    }),
  },
}));

const TextMaskCustom = React.forwardRef((props: any, _: any) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="(#00) 000-0000"
      definitions={{
        "#": /[2-9]/,
      }}
      onAccept={(value: any) =>
        onChange({ target: { name: props.name, value } })
      }
      overwrite
    />
  );
});

const Confirm = ({
  selectedDateTime,
  selectedServices,
  setSelectedServices,
  bookingForm,
  setBookingForm,
  isValidForm,
  customerInfo,
  setCustomerInfo,
  timezone,
  unixPickDateTime,
  bookingSettings,
}: IProps) => {
  const classes = useStyles();
  const { sendJsonMessage } = useWebSocket(
    websocketServices.getPosWebsocketConnectionUrl(),
    {
      onError: async () => {
        const { refreshToken } = JSON.parse(
          sessionStorage.getItem(GUEST_INFO) || "{}"
        );
        const res = await authServices.refreshToken({ refreshToken });
        if (res?.data?.success) {
          const guestInfo = JSON.parse(
            sessionStorage.getItem(GUEST_INFO) || "{}"
          );
          guestInfo.accessToken = res?.data?.data;
          sessionStorage.setItem(GUEST_INFO, JSON.stringify(guestInfo));
        }
      },
      shouldReconnect: () => {
        return true;
      },
      reconnectAttempts: 10,
      reconnectInterval: 1000,
      queryParams: {
        authorizer: JSON.parse(sessionStorage.getItem(GUEST_INFO) || "{}")
          .accessToken,
        storeId: sessionStorage.getItem(X_Tenant_Id) || "",
      },
    }
  );

  const servicesRef = useRef<HTMLDivElement>(null);
  const customerInfoRef = useRef<HTMLDivElement>(null);
  const [loadingCustomer, setLoadingCustomer] = useState<boolean>(false);
  const { includeBufferTime, confirmRequired, showBookForSomeoneElse } =
    bookingSettings;
  const [isBookingForSomeone, setIsBookingForSomeone] =
    useState<boolean>(false);

  const [openCalendar, setOpenCalendar] = useState(false);

  const date = moment(selectedDateTime.date).startOf("d").unix();

  // const dateStr = moment(selectedDateTime.date).format("YYYY-MM-DD"); // Example date
  // const timeStr = selectedDateTime.time;
  // const datetimeStr = `${dateStr} ${timeStr}`;
  // const datetime = moment.tz(datetimeStr, "YYYY-MM-DD hh:mm A", timezone);
  // const unixPickDateTime = datetime.unix();
  // const unixPickDateTime = calcUnixDateTime(
  //   selectedDateTime.date,
  //   selectedDateTime.time,
  //   timezone
  // );

  const handleChangeForm =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const validValue = e.target.value.replace(/\s{2,}/g, " ");
      setBookingForm({
        submitting: false,
        submitted: false,
        data: { ...bookingForm.data, [field]: validValue.trimStart() },
      });
    };

  const handleChangeBirthday = (data: Moment | null) => {
    const fixedYearValue = data ? data.clone().year(2000) : null;

    setBookingForm({
      ...bookingForm,
      data: {
        ...bookingForm.data,
        birthday: fixedYearValue,
      },
    });
  };

  const handleSubmit = async () => {
    setBookingForm({ ...bookingForm, submitting: true });
    try {
      const currentTime = moment().tz(timezone).unix();
      const services = selectedServices.map((service, idx) => {
        const startTime = calStartTimeOfService(
          selectedServices,
          service.staffId,
          unixPickDateTime,
          idx,
          includeBufferTime
        );
        const duration =
          service.time + (includeBufferTime ? service.bufferTime : 0);

        return {
          id: service.serviceId,
          name: service.serviceName,
          staffId: service.staffId,
          duration,
          startTime,
          endTime: moment(+startTime * 1000)
            .add(
              service.time + (includeBufferTime ? service.bufferTime : 0),
              "minutes"
            )
            .unix()
            .toString(),
          request: true,
          fixedStartTime: `${moment(selectedDateTime.date).format(
            "YYYY-MM-DD"
          )}T${convertTo24Hour(
            selectedDateTime.time,
            selectedServices
              .slice(0, idx)
              .reduce(
                (total, selectedService) =>
                  total +
                  selectedService.time +
                  (includeBufferTime ? selectedService.bufferTime : 0),
                0
              )
          )}`,
          fixedEndTime: `${moment(selectedDateTime.date).format(
            "YYYY-MM-DD"
          )}T${convertTo24Hour(
            selectedDateTime.time,
            selectedServices
              .slice(0, idx)
              .reduce(
                (total, selectedService) =>
                  total +
                  selectedService.time +
                  (includeBufferTime ? selectedService.bufferTime : 0),
                0
              ) + duration
          )}`,
        };
      });

      const data = {
        id: uuidv4(),
        customerId: customerInfo?.customerId || uuidv4(),
        customerName: bookingForm.data.name,
        customerPhone: bookingForm.data.phone,
        customerEmail: bookingForm.data.email,
        customerNote: customerInfo?.note || "",
        date,
        status: "",
        note: bookingForm.data.comments,
        services,
        type:
          //&& !!customerInfo?.blacklisted
          confirmRequired
            ? "REQUESTED"
            : currentTime > +services[0].startTime + 600
            ? "LATE"
            : "ON_TIME",
        createdDate: currentTime.toString(),
        source: BOOKING_SOURCE.ONLINE,
        createdBy: "Customer",
        updatedBy: "",
        updatedDate: currentTime.toString(),
        ...(showBookForSomeoneElse &&
          isBookingForSomeone && {
            bookingFor: bookingForm.data.lastName + bookingForm.data.firstName,
          }),
        customerVisits: customerInfo?.visits || 0,
        customerBirthDate:
          customerInfo?.birthDate ||
          (bookingForm.data.birthday
            ? moment(bookingForm.data.birthday).format("DD")
            : null) ||
          "",
        customerBirthMonth:
          customerInfo?.birthMonth ||
          (bookingForm.data.birthday
            ? moment(bookingForm.data.birthday).format("MM")
            : null) ||
          "",
        isRead: false,
      };
      const createRes = await appointmentServices.createAppointment(data);

      if (!createRes?.data?.success)
        throw new Error(
          "Failed to book appointment. Please refresh and try again!"
        );
      const { customerCreatedAt } = createRes.data.data;
      sendJsonMessage({
        action: WS_ACTION.BOOKING_RESOLVER,
        identify: "handleCreateBooking",
        eventName: WS_ACTION_TYPE.CREATE_BOOKING,
        data: {
          tenantId: sessionStorage.getItem(X_Tenant_Id),
          ...data,
          customerCreatedAt,
        },
      });
    } catch (error: any) {
      window.alert(
        error?.msg ||
          error?.message ||
          "Something went wrong. Please try again!"
      );
      console.log(error);
    }
    setBookingForm({ ...bookingForm, submitting: false, submitted: true });
  };

  const getCustomerInfoByPhone = async (phone: string) => {
    setLoadingCustomer(true);
    try {
      const response = await businessServices.getCustomerByPhone(phone);
      if (!response?.data?.success) throw new Error("");

      const data = response?.data?.data || {};
      setCustomerInfo({ ...data, phone });
      if (!data?.customerId) {
        setBookingForm({
          ...bookingForm,
          data: {
            ...bookingForm.data,
            name: "",
            email: "",
          },
        });
      }
    } catch (error) {
      console.log("error onSubmitPhone:>> ", error);
    }
    setLoadingCustomer(false);
  };

  const handleRemoveService = (serviceId: string) => {
    if (selectedServices.length === 1) return;
    setSelectedServices(
      selectedServices.filter((service) => service.serviceId !== serviceId)
    );
  };

  useEffect(() => {
    if (isValidPhoneNumber(bookingForm.data.phone)) {
      const phoneNumber = convertToPhoneNumber(bookingForm.data.phone).join("");
      if (phoneNumber !== customerInfo?.phone)
        getCustomerInfoByPhone(phoneNumber);
    }
  }, [bookingForm.data.phone]);

  useEffect(() => {
    let _birthday = null;

    if (customerInfo?.birthDate && customerInfo?.birthMonth) {
      _birthday = moment(
        `2000-${customerInfo.birthMonth}-${customerInfo.birthDate}`,
        "YYYY-MM-DD"
      );
    }
    setBookingForm({
      ...bookingForm,
      data: {
        ...bookingForm.data,
        name: customerInfo?.firstName || bookingForm.data.name || "",
        email: customerInfo?.email || bookingForm.data.email || "",
        birthday: _birthday,
      },
    });
  }, [customerInfo?.customerId]);

  useEffect(() => {
    if (servicesRef?.current) {
      const { clientHeight } = servicesRef.current;
      if (customerInfoRef.current)
        customerInfoRef.current.style.height = `calc(100% - ${
          clientHeight + 34
        }px)`;
    }
  }, [servicesRef?.current?.clientHeight, selectedServices.length]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        animation: "0.75s slide-left",
      }}
    >
      <Box px={2} height="100%">
        <ServicesInformation
          ref={servicesRef}
          date={selectedDateTime.date as Moment}
          unixPickDateTime={unixPickDateTime}
          services={selectedServices}
          includeBufferTime={includeBufferTime}
          onRemove={handleRemoveService}
        />
        <Stack
          gap={3}
          pt={2}
          mt={2}
          ref={customerInfoRef}
          height="100%"
          overflow="auto"
        >
          <Box>
            <CustomTextField
              label="Phone"
              fullWidth
              placeholder="Enter phone number"
              value={bookingForm.data.phone}
              onChange={handleChangeForm("phone")}
              autoComplete="off"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputComponent: TextMaskCustom,
                disabled: loadingCustomer,
                endAdornment: (
                  <InputAdornment position="end">
                    {loadingCustomer && <CircularProgress size={20} />}
                  </InputAdornment>
                ),
                spellCheck: "false",
              }}
            />
            {/* {!!bookingForm.error.phone && (
            <Typography fontSize="12px" color="#ed6d53" mt="5px">
              {bookingForm.error.phone}
            </Typography>
          )} */}
          </Box>
          <Box>
            <CustomTextField
              label="Name"
              fullWidth
              placeholder="Enter name"
              value={bookingForm.data.name}
              onChange={handleChangeForm("name")}
              autoComplete="off"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                readOnly: !!customerInfo?.customerId || loadingCustomer,
                disabled: !isValidPhoneNumber(bookingForm.data.phone),
                spellCheck: "false",
              }}
            />
            {/* {!!bookingForm.error.name && (
            <Typography fontSize="12px" color="#ed6d53" mt="5px">
              {bookingForm.error.name}
            </Typography>
          )} */}
          </Box>
          <Box>
            <CustomTextField
              label="Email"
              fullWidth
              placeholder="Enter email address"
              value={bookingForm.data.email}
              onChange={handleChangeForm("email")}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                readOnly: !!customerInfo?.email || loadingCustomer,
                disabled: !isValidPhoneNumber(bookingForm.data.phone),
                spellCheck: "false",
              }}
            />
            {/* {!!bookingForm.error.email && (
            <Typography fontSize="12px" color="#ed6d53" mt="5px">
              {bookingForm.error.email}
            </Typography>
          )} */}
          </Box>
          <Box>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              dateFormats={{ monthAndYear: "MMMM" }}
            >
              <DesktopDatePicker
                label="Birthday"
                value={bookingForm.data.birthday}
                open={openCalendar}
                onOpen={() => setOpenCalendar(true)}
                onClose={() => setOpenCalendar(false)}
                inputFormat="MM/DD"
                views={["month", "day"]}
                openTo="month"
                onChange={handleChangeBirthday}
                disabled={!isValidPhoneNumber(bookingForm.data.phone)}
                readOnly={
                  (!!customerInfo?.birthDate && !!customerInfo?.birthMonth) ||
                  loadingCustomer
                }
                renderInput={(params) => {
                  const isReadOnly =
                    (!!customerInfo?.birthDate && !!customerInfo?.birthMonth) ||
                    loadingCustomer;
                  return (
                    <CustomTextField
                      {...params}
                      onClick={() => {
                        if (
                          !isReadOnly &&
                          isValidPhoneNumber(bookingForm.data.phone)
                        ) {
                          setOpenCalendar(true);
                        }
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                        onPaste: (e) => e.preventDefault(),
                        onKeyDown: (e) => e.preventDefault(),
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!isReadOnly && bookingForm.data.birthday ? (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChangeBirthday(null);
                                }}
                                size="small"
                              >
                                <ClearIcon />
                              </IconButton>
                            ) : (
                              params?.InputProps?.endAdornment
                            )}
                          </>
                        ),
                        readOnly: isReadOnly,
                        disabled: !isValidPhoneNumber(bookingForm.data.phone),
                      }}
                    />
                  );
                }}
                PopperProps={{
                  disablePortal: true,
                  placement: "auto",
                }}
              />
            </LocalizationProvider>
          </Box>
          {/* <Box width="100%">
            <Typography mb="5px">Comments</Typography>
            <FieldTextarea
              placeholder="Enter any comments"
              value={bookingForm.data.comments}
              onChange={handleChangeForm("comments")}
            />
          </Box> */}
          {showBookForSomeoneElse && (
            <Box>
              <FormControlLabel
                sx={{ margin: 0 }}
                control={
                  <IOSSwitch
                    value={isBookingForSomeone}
                    onChange={() =>
                      setIsBookingForSomeone((prevState) => !prevState)
                    }
                  />
                }
                label="I am booking for someone else"
              />
              <Box
                sx={{
                  maxHeight: isBookingForSomeone ? "200px" : 0,
                  opacity: isBookingForSomeone ? 1 : 0,
                  transition: "all 0.3s ease",
                }}
              >
                <Box py={3}>
                  <Typography fontSize={14}>
                    Please enter the name of the person you are booking for:
                  </Typography>
                  <Grid container columnSpacing={2} mt={3}>
                    <Grid item xs={6}>
                      <CustomTextField
                        label="First name"
                        fullWidth
                        placeholder="Enter first name"
                        value={bookingForm.data.firstName}
                        onChange={handleChangeForm("firstName")}
                        autoComplete="off"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          spellCheck: "false",
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomTextField
                        label="Last name"
                        fullWidth
                        placeholder="Enter last name"
                        value={bookingForm.data.lastName}
                        onChange={handleChangeForm("lastName")}
                        autoComplete="off"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          spellCheck: "false",
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}
          <Box>
            <CustomTextField
              label="Comments"
              fullWidth
              placeholder="Enter any comments"
              value={bookingForm.data.comments}
              onChange={handleChangeForm("comments")}
              autoComplete="off"
              rows={4}
              multiline
              InputLabelProps={{ shrink: true }}
              InputProps={{
                spellCheck: "false",
              }}
            />
          </Box>
          <Button
            variant="contained"
            className={classes.btnBooking}
            onClick={handleSubmit}
            disabled={
              bookingForm.submitting ||
              !isValidForm ||
              loadingCustomer ||
              (showBookForSomeoneElse &&
                isBookingForSomeone &&
                (!bookingForm.data.firstName || !bookingForm.data.lastName))
            }
          >
            {bookingForm.submitting ? (
              <CircularProgress size="25px" color="inherit" />
            ) : (
              "Book Now"
            )}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default Confirm;
